import type { Building } from "building-types/building/buildingTypes";
import type { Doc } from "building-types/doc";
import type { EdgeDimensionBehavior } from "building-types/edgeBehavior/edgeDimensionBehavior";
import type { HalfEdgeAssociationTable } from "building-types/planarGraphs/planarGraphAssociationTypes";
import type { Room } from "building-types/rooms/roomTypes";
import { RoomKind, RoomLabelRounding, RoomLabelUnits, RoomType } from "building-types/rooms/roomTypesEnum";
import type { PlanSettings } from "building-types/settings/planSettings";
import type { GeneratedRailing } from "building-types/settings/railingTypes";
import { ButtCapMode } from "building-types/walls/wallEndsTypes";
import { WallType } from "building-types/walls/wallEnums";
import { WallLayerType } from "building-types/walls/wallEnums";
import type { StartSortedWallGaps } from "building-types/walls/wallGap";
import type { WallMassSystem } from "building-types/walls/wallSytemTypes";
import type { SolidWall, SolidWallMass, Wall } from "building-types/walls/wallTypes";
import { SolidWallKind, WallLOD } from "building-types/walls/wallTypes";
import type { Table } from "common/data/table";
import type { InchesSquared } from "common/types/nominalNumbersTypes";
import { feetSquaredFromInchesSquared, inchesSquaredNew } from "common/utils/nominalNumbers";
import { isCompositeLevelContentSourceId, resolveContentSourceIdToLevelIds } from "doc/docViewport";
import { isInteriorRoomEnclosure, isWalledRoomEnclosure } from "doc/rooms/roomAttributesTools";
import { roomIsEffectivelyExterior } from "doc/rooms/roomQuery";
import { closestParamNormalizedLine2, closestPointPolyLine2 } from "geom/closestPoint";
import {
  classifyContainmentPolyLine2PolyLine2,
  classifyContainmentVec2PolyLine2,
  PolyLine2PolyLine2ContainmentClass,
  Vec2PolyLine2ContainmentClass,
} from "geom/containment";
import { DIST_EPSILON, ONE_MINUS_DIST_EPSILON, ZERO_EPSILON, WindingDir } from "geom/core";
import { line2Collinear, line2Eval } from "geom/line2";
import type { OBB2 } from "geom/obb2";
import { offsetPolyLine2 } from "geom/offset";
import type { PlanarGraph } from "geom/planarGraph";
import {
  planarGraphAdjacentHalfEdge,
  planarGraphHalfEdgeNormal,
  planarGraphHalfEdgeTangent,
  planarGraphTraceFaceGroupPerimeter,
} from "geom/planarGraph";
import { polygonUnion } from "geom/polygon";
import {
  polyLine2Area,
  polyLine2ByTwoPoints,
  polyLine2Copy,
  polyLine2Edge,
  polyLine2EdgeCount,
  polyLine2EdgeEval,
  polyLine2EdgeNormal,
  polyLine2Length,
  polyLine2Reverse,
  polyLine2SetCCW,
  polyLine2WindingDir,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine2New, type PolyLine2Polygon } from "geom/polyLine2Types";
import { splitPolyLine2ByRegions } from "geom/trim";
import { vec2Dist, vec2Dot, vec2Scale } from "geom/vec2";
import { groundLevelIdForLevelStack, isGroundLevel } from "../elements/level/levelTools";
import { determineWallKind, solidWallWithOpenings } from "../elements/walls/solidWall";
import { solidWallGaps } from "../elements/walls/solidWallCrossSectionUtils";
import { getWallPath } from "../elements/walls/wall";
import { solidWallSubstrateLayer, wallCrossSectionLayers } from "../elements/walls/wallLayers";
import { wallSideNew } from "../elements/walls/wallSideBasics";
import { makeWallMassSystemCustom } from "../elements/walls/wallSystem";
import { getPlanarGraphFaces } from "./planarGraphPerimeters";

export function makeStairWellWallMassSystem(
  building: Pick<Building, "planarGraphs" | "walls" | "settings">,
  levelId: string,
) {
  const { planarGraphs, walls, settings } = building;

  const planarGraph = planarGraphs[levelId].planarGraph;
  const wallSystem = walls[levelId];
  const wallSettings = settings.wall;

  const wallPredicate = (wall: Wall) => {
    if (wall.wallType !== WallType.SOLID) {
      return false;
    }

    const wallPath = getWallPath(wall);
    const lStair = wallPath.leftRoom?.type === RoomType.STAIRS;
    const rStair = wallPath.rightRoom?.type === RoomType.STAIRS;

    return lStair || rStair;
  };

  const wallOffset = (wall: Wall) => {
    if (wall.wallType === WallType.VOID) {
      return { leftOffset: wall.leftOffset, rightOffset: wall.rightOffset };
    }

    const mass = solidWallSubstrateLayer(wall);
    return { leftOffset: mass.leftOffset, rightOffset: mass.rightOffset };
  };

  // new wall mass system with only stair walls
  return makeWallMassSystemCustom({
    planarGraph,
    wallSystem,
    wallSettings,
    wallPredicate,
    wallOffset,
    wallButtCapMode: ButtCapMode.END_MITER_EXTEND,
  });
}

interface RailingLinework {
  readonly solid: PolyLine2[];
  readonly dashed: PolyLine2[];
  readonly solidOBBs: OBB2[];
  readonly dashedOBBs: OBB2[];
  readonly solidPost: PolyLine2[];
  readonly solidPostOBBs: OBB2[];
  readonly railing: GeneratedRailing;
}

export function makeRailingLineworkForLevel(railings: Table<GeneratedRailing>, sLevelId: string): RailingLinework[] {
  const result: RailingLinework[] = [];

  for (const railingId in railings) {
    const railing = railings[railingId];
    const solid: PolyLine2[] = [];
    const dashed: PolyLine2[] = [];
    const solidPost: PolyLine2[] = [];
    const solidOBBs: OBB2[] = [];
    const dashedOBBs: OBB2[] = [];
    const solidPostOBBs: OBB2[] = [];

    // TODO: calculate OBBs for solid, dashed, and solidPost

    for (const key in railing.railingLinework2) {
      const levelLinework = railing.railingLinework2[key];
      const { levelId, polyLine2s, polyLine2sAboveViewCut, postPolyLine2s, obbs, obbsAboveViewCut } = levelLinework;
      if (levelId !== sLevelId) {
        continue;
      }

      solid.push(...polyLine2s);
      dashed.push(...polyLine2sAboveViewCut);
      solidPost.push(...postPolyLine2s.map((post) => post.polyLine2));
      solidOBBs.push(...obbs);
      dashedOBBs.push(...obbsAboveViewCut);
      solidPostOBBs.push(...postPolyLine2s.map((post) => post.obb));
    }

    result.push({ solid, dashed, solidPost, railing, solidOBBs, dashedOBBs, solidPostOBBs });
  }

  return result;
}

interface PerimeterWallInfo {
  offsetExterior: PolyLine2[];
  exteriorWallSegments: PolyLine2[];
  exteriorWallHalfEdges: number[][];
  interiorWallSegments: PolyLine2[];
}

export function makeExteriorWallSegments({
  building,
  levelId,
  includeDoors,
  includeWindows,
  includeExteriorRooms,
  wallLayer,
  dimensionBehavior,
  excludeUndimensionedOpenings = false,
  excludeNonParallelWalls = false,
}: {
  building: Pick<
    Building,
    | "levels"
    | "planarGraphs"
    | "mirror"
    | "rooms"
    | "roomLevelIndices"
    | "roomsByLevel"
    | "settings"
    | "walls"
    | "floorPlates"
    | "dimensionBehavior"
  > & { doc: Pick<Doc, "compositeLevels" | "levels"> };
  levelId: string;
  includeDoors: boolean;
  includeWindows: boolean;
  includeExteriorRooms: boolean;
  /**
   * We only allow these Layers for now because any other will
   * produce unwanted results. FINISH is not a user facing layer at this time.
   */
  wallLayer: WallLayerType.STRUCTURE | WallLayerType.SUBSTRATE;
  /**
   * Ignored for composite level IDs — each member level's dimension behavior is resolved
   * internally from `building.dimensionBehavior`. May be omitted when calling with a composite
   * level ID.
   */
  dimensionBehavior?: HalfEdgeAssociationTable<EdgeDimensionBehavior>;
  excludeUndimensionedOpenings?: boolean;
  excludeNonParallelWalls?: boolean;
}): PerimeterWallInfo {
  if (isCompositeLevelContentSourceId(building.doc, levelId)) {
    return makeCompositeExteriorWallSegments({
      building,
      compositeLevelId: levelId,
      includeDoors,
      includeWindows,
      includeExteriorRooms,
      wallLayer,
      excludeUndimensionedOpenings,
      excludeNonParallelWalls,
    });
  }
  return makeLevelExteriorWallSegments({
    building,
    levelId,
    includeDoors,
    includeWindows,
    includeExteriorRooms,
    wallLayer,
    dimensionBehavior: dimensionBehavior ?? building.dimensionBehavior[levelId],
    excludeUndimensionedOpenings,
    excludeNonParallelWalls,
  });
}

function makeLevelExteriorWallSegments({
  building,
  levelId,
  includeDoors,
  includeWindows,
  includeExteriorRooms,
  wallLayer,
  dimensionBehavior,
  excludeUndimensionedOpenings = false,
  excludeNonParallelWalls = false,
}: {
  building: Pick<
    Building,
    | "levels"
    | "planarGraphs"
    | "mirror"
    | "rooms"
    | "roomLevelIndices"
    | "roomsByLevel"
    | "settings"
    | "walls"
    | "floorPlates"
  >;
  levelId: string;
  includeDoors: boolean;
  includeWindows: boolean;
  includeExteriorRooms: boolean;
  /**
   * We only allow these Layers for now because any other will
   * produce unwanted results. FINISH is not a user facing layer at this time.
   */
  wallLayer: WallLayerType.STRUCTURE | WallLayerType.SUBSTRATE;
  dimensionBehavior: HalfEdgeAssociationTable<EdgeDimensionBehavior>;
  excludeUndimensionedOpenings?: boolean;
  excludeNonParallelWalls?: boolean;
}): PerimeterWallInfo {
  const { walls } = building;
  const levelWalls = walls[levelId];
  const groundLevel = groundLevelIdForLevelStack(building.levels, levelId);
  if (!groundLevel) {
    return {
      offsetExterior: [],
      exteriorWallSegments: [],
      exteriorWallHalfEdges: [],
      interiorWallSegments: [],
    };
  }

  const wallConfig = building.settings.wall;

  const exteriorWallSegments: PolyLine2[] = [];
  const interiorWallSegments: PolyLine2[] = [];
  const planarGraph = building.planarGraphs[levelId].planarGraph;

  const { rooms, roomLevelIndices, roomsByLevel, planarGraphs, mirror } = building;

  const exteriorFaces = getPlanarGraphFaces(
    rooms,
    roomLevelIndices,
    roomsByLevel[levelId],
    planarGraphs[levelId],
    (room) =>
      includeExteriorRooms
        ? isWalledRoomEnclosure[room.attributes.roomEnclosure]
        : isInteriorRoomEnclosure[room.attributes.roomEnclosure],
  );

  const exteriorBoundaries = planarGraphTraceFaceGroupPerimeter(planarGraphs[levelId].planarGraph, exteriorFaces);

  // this may happen when there are no exterior walls (eg in a gazebo)
  if (exteriorBoundaries.faceVertices.length === 0) {
    return {
      offsetExterior: [],
      exteriorWallSegments: [],
      exteriorWallHalfEdges: [],
      interiorWallSegments: [],
    };
  }

  // this wall mass system will convert exterior walls to void when calculating
  // the exterior wall segments without exterior rooms, and will create solid
  // walls at void walls for accurately creating dimensions around void wall perimeters
  const wallMassSystem = makeWallMassSystemCustom({
    planarGraph,
    wallSystem: levelWalls,
    wallSettings: wallConfig,
    // only keep walls that have at least one interior room
    wallPredicate: (wall) => {
      const { leftRoom, rightRoom } = getWallPath(wall);
      const leftExterior = roomIsEffectivelyExterior(leftRoom, includeExteriorRooms);
      const rightExterior = roomIsEffectivelyExterior(rightRoom, includeExteriorRooms);
      if (wall.wallType !== WallType.SOLID) {
        if (building.levels[levelId].floorIndex < 0) {
          return false;
        }
        return leftExterior !== rightExterior;
      }
      return !(leftExterior && rightExterior);
    },
    // map the original offsets to the new wall masses, and assign offsets to void walls
    // we use the WallLOD.L1 here because we want void wall offsets for edge alignment
    wallOffset: (wall) => {
      const wallCrossSections = wallCrossSectionLayers(wall, wallLayer, WallLOD.L1);
      if (!wallCrossSections.length) {
        return { leftOffset: 0, rightOffset: 0 };
      }
      let maxLeftOffset = -Infinity;
      let maxRightOffset = -Infinity;
      for (const wallCrossSection of wallCrossSections) {
        const { leftOffset, rightOffset } = wallCrossSection;
        maxLeftOffset = Math.max(maxLeftOffset, leftOffset);
        maxRightOffset = Math.max(maxRightOffset, rightOffset);
      }
      return { leftOffset: maxLeftOffset, rightOffset: maxRightOffset };
    },
    // we need to remap the wall paths to strip rooms that are deemed exterior for
    // the purposes of these wall edges
    wallPathRemapFunc: (wallPath) => {
      let { leftRoomKind, rightRoomKind } = wallPath;
      let leftRoom: Room | undefined = wallPath?.leftRoom;
      let rightRoom: Room | undefined = wallPath?.rightRoom;

      const leftExterior = roomIsEffectivelyExterior(leftRoom, includeExteriorRooms);
      const rightExterior = roomIsEffectivelyExterior(rightRoom, includeExteriorRooms);
      if (leftExterior) {
        leftRoom = undefined;
        leftRoomKind = RoomKind.BOUNDARY;
      }
      if (rightExterior) {
        rightRoom = undefined;
        rightRoomKind = RoomKind.BOUNDARY;
      }
      return { ...wallPath, leftRoom, leftRoomKind, rightRoom, rightRoomKind };
    },
  });

  const exteriorWallHalfEdges: number[][] = [];

  for (let face = 0, faceC = exteriorBoundaries.faceHalfEdges.length; face < faceC; face++) {
    const polyLineVerts: number[] = [];
    const polyLineHalfEdges: number[] = [];

    let firstWall: SolidWall | undefined = undefined;
    let lastWall: SolidWall | undefined = undefined;
    // let lastWallFoundSolidWall = false; // not needed currently

    let firstWallFoundWallChange = false;

    const perimHalfEdges = exteriorBoundaries.faceHalfEdges[face];
    const { halfEdges } = planarGraph;

    for (const perimHalfEdge of perimHalfEdges) {
      const vi = halfEdges[perimHalfEdge * 2];
      const { foundSolidWall, nOffset, foundWallChange, disableDimension } = nonParallelSolidWallAtVertex(
        planarGraph,
        wallMassSystem,
        vi,
        perimHalfEdge,
        excludeNonParallelWalls || dimensionBehavior,
      );
      const wall = levelWalls.walls[levelWalls.halfEdgeToWall[perimHalfEdge]];
      const wallMass = wallMassSystem.walls[wallMassSystem.halfEdgeToWall[perimHalfEdge]];
      if (wallMass.wallType !== WallType.SOLID_MASS) {
        continue;
      }

      // create the solid wall for dimensions from the wallMass
      let solidWall: SolidWall;
      if (wall.wallType === WallType.SOLID) {
        // if the wall was originally solid, we copy most of the properties
        solidWall = {
          ...wall,
          mass: wallMass,
        };
      } else {
        // otherwise we need to create a new wall
        solidWall = solidWallWithOpenings({
          id: wall.id,
          path: wallMass.path,
          mass: wallMass,
          issues: [],
          leftSide: wallSideNew(),
          rightSide: wallSideNew(),
          levelId: levelId,
          mirror,
          wallMassSystem,
        });
      }

      let segments = Array.from(
        wallGapSegments(solidWall, includeDoors, includeWindows, includeExteriorRooms, excludeUndimensionedOpenings),
      );
      let segmentHalfEdges = new Array(segments.length).fill(perimHalfEdge);

      // We rely on the next half edge to add the end points for us.  That way, if there's a void wall
      // up there, we can skip it.
      if (lastWall && !lastWall.mass.start.isFreeEnd) {
        polyLineHalfEdges.pop();
        polyLineHalfEdges.pop();
        if (foundWallChange) {
          polyLineHalfEdges.push(perimHalfEdge);
          polyLineHalfEdges.push(perimHalfEdge);
        } else {
          polyLineVerts.pop();
          polyLineVerts.pop();
        }
      }

      // Void wall or disabled dimension, so don't add starting verts.
      // Leave starting verts from the first wall since they are used in the cleanup
      if ((!foundSolidWall && !solidWall.mass.end.isFreeEnd) || disableDimension) {
        segments = segments.slice(2);
        segmentHalfEdges = segmentHalfEdges.slice(2);
      } else {
        // Offset to edge of wall face.
        const [tx, ty] = planarGraphHalfEdgeTangent(planarGraph, perimHalfEdge);
        const [offX, offY] = vec2Scale(tx, ty, nOffset);
        segments[0] += offX;
        segments[1] += offY;
      }

      polyLineVerts.push(...segments);
      polyLineHalfEdges.push(...segmentHalfEdges);

      lastWall = solidWall;
      // lastWallFoundSolidWall = foundSolidWall; // not needed currently

      if (!firstWall) {
        firstWall = lastWall;
        firstWallFoundWallChange = foundWallChange;
      }
    }

    // clean up the loop
    if (lastWall && !lastWall.mass.start.isFreeEnd) {
      if (!firstWallFoundWallChange) {
        polyLineHalfEdges.pop();
        polyLineHalfEdges.pop();
        polyLineVerts.pop();
        polyLineVerts.pop();
      }
    }

    // close the polyline
    polyLineVerts.push(polyLineVerts[0]);
    polyLineVerts.push(polyLineVerts[1]);

    const polyline = polyLine2New(polyLineVerts);

    const loopWindingDir = polyLine2WindingDir(polyline);

    // the exterior loop will be CW, interior loops will be CCW
    if (loopWindingDir === WindingDir.CW) {
      // We always return the result as CCW.
      polyLine2Reverse(polyline);
      polyLineHalfEdges.reverse();

      exteriorWallSegments.push(polyline);
    } else if (loopWindingDir === WindingDir.CCW) {
      if (polyLineHalfEdges.length) {
        // Holes in the planar graph should not be considered for auto-dimensioning as the system doesn't
        // detect or support them. We need to detect and remove them. We do this by classifying the loop appropriately.

        // If the the loop is CCW, it can either be:
        //  1. a hole
        //  2. an exterior type room (eg a portico)
        const wallId = levelWalls.halfEdgeToWall[polyLineHalfEdges[0]];

        if (wallId) {
          const wall = levelWalls.walls[wallId];

          // the loop is not a boundary, so we know it is an exterior type room
          if (wall && getWallPath(wall).leftRoomKind !== RoomKind.BOUNDARY) {
            exteriorWallSegments.push(polyline);
          }
        }
      }
    }

    polyLineHalfEdges.push(polyLineHalfEdges[0]);
    polyLineHalfEdges.push(polyLineHalfEdges[1]);

    if (!includeExteriorRooms) {
      for (let i = 0, l = polyLine2EdgeCount(polyline); i < l; i++) {
        const plEdge = polyLine2Edge(polyline, i);
        const plHalfEdge = polyLineHalfEdges[i * 2];
        // this edge comes from a thickness offset
        if (plHalfEdge === null) {
          continue;
        }
        const plWall = levelWalls.walls[levelWalls.halfEdgeToWall[plHalfEdge]];
        const { leftRoom, rightRoom } = getWallPath(plWall);
        if (
          leftRoom &&
          rightRoom &&
          isWalledRoomEnclosure[leftRoom.attributes.roomEnclosure] &&
          isWalledRoomEnclosure[rightRoom.attributes.roomEnclosure]
        ) {
          interiorWallSegments.push(plEdge);
        }
      }
    }

    exteriorWallHalfEdges.push(polyLineHalfEdges);
  }

  const levelWallPerimeter = exteriorWallSegments[0];
  const groundLevelPlanarGraphPerimeter = building.floorPlates[groundLevel]?.perimeterPolyLine2s[0];

  const groundLevelWallOffset =
    wallConfig.exteriorWallOffset +
    (wallLayer === WallLayerType.SUBSTRATE ? wallConfig.exteriorWallSubstrateThickness : 0);
  const groundLevelWallPerimeter = groundLevelPlanarGraphPerimeter
    ? offsetPolyLine2(groundLevelPlanarGraphPerimeter, -groundLevelWallOffset)
    : undefined;

  let exterior = isGroundLevel(building.levels[levelId], building.levels)
    ? levelWallPerimeter
    : groundLevelWallPerimeter;

  // for cases where the first floor has no covered area but we are operating
  // on the second floor
  exterior = exterior ?? levelWallPerimeter;

  return {
    offsetExterior: [exterior],
    exteriorWallSegments,
    exteriorWallHalfEdges,
    interiorWallSegments,
  };
}

function makeCompositeExteriorWallSegments({
  building,
  compositeLevelId,
  includeDoors,
  includeWindows,
  includeExteriorRooms,
  wallLayer,
  excludeUndimensionedOpenings = false,
  excludeNonParallelWalls = false,
}: {
  building: Pick<
    Building,
    | "levels"
    | "planarGraphs"
    | "mirror"
    | "rooms"
    | "roomLevelIndices"
    | "roomsByLevel"
    | "settings"
    | "walls"
    | "floorPlates"
    | "dimensionBehavior"
  > & { doc: Pick<Doc, "compositeLevels" | "levels"> };
  compositeLevelId: string;
  includeDoors: boolean;
  includeWindows: boolean;
  includeExteriorRooms: boolean;
  wallLayer: WallLayerType.STRUCTURE | WallLayerType.SUBSTRATE;
  excludeUndimensionedOpenings?: boolean;
  excludeNonParallelWalls?: boolean;
}): PerimeterWallInfo {
  const memberLevelIds = resolveContentSourceIdToLevelIds(building.doc, compositeLevelId, building.levels).filter(
    (id) => building.walls[id],
  );

  if (memberLevelIds.length === 0) {
    return {
      offsetExterior: [],
      exteriorWallSegments: [],
      exteriorWallHalfEdges: [],
      interiorWallSegments: [],
    };
  }

  const allExteriorSegments: PolyLine2[] = [];
  const allExteriorHalfEdges: number[][] = [];
  const allInteriorSegments: PolyLine2[] = [];
  const allOffsetExterior: PolyLine2[] = [];

  for (const memberLevelId of memberLevelIds) {
    const result = makeLevelExteriorWallSegments({
      building,
      levelId: memberLevelId,
      includeDoors,
      includeWindows,
      includeExteriorRooms,
      wallLayer,
      dimensionBehavior: building.dimensionBehavior[memberLevelId],
      excludeUndimensionedOpenings,
      excludeNonParallelWalls,
    });

    allExteriorSegments.push(...result.exteriorWallSegments);
    allExteriorHalfEdges.push(...result.exteriorWallHalfEdges);
    allInteriorSegments.push(...result.interiorWallSegments);
    allOffsetExterior.push(...result.offsetExterior);
  }

  if (allExteriorSegments.length <= 1) {
    return {
      offsetExterior: allOffsetExterior,
      exteriorWallSegments: allExteriorSegments,
      exteriorWallHalfEdges: allExteriorHalfEdges,
      interiorWallSegments: allInteriorSegments,
    };
  }

  try {
    const compositeLoops = polygonUnion(allExteriorSegments.map((seg) => [polyLine2SetCCW(polyLine2Copy(seg))]))
      .map((poly: PolyLine2Polygon) => poly[0])
      .filter((loop): loop is PolyLine2 => !!loop && polyLine2Area(loop) > ZERO_EPSILON);

    if (compositeLoops.length === 0) {
      return {
        offsetExterior: allOffsetExterior,
        exteriorWallSegments: allExteriorSegments,
        exteriorWallHalfEdges: allExteriorHalfEdges,
        interiorWallSegments: allInteriorSegments,
      };
    }
    const constructedExteriorPolyLines: PolyLine2[] = [];
    const constructedExteriorHalfEdges: number[][] = [];

    for (const unionLoop of compositeLoops) {
      const { constituentSegments, constituentHalfEdges } = extractExteriorSegmentsFromCompositeLoop(
        allExteriorSegments,
        allExteriorHalfEdges,
        unionLoop,
      );

      if (constituentSegments.length === 0) {
        continue;
      }

      const sorted = sortSegmentsByLoopPosition(constituentSegments, unionLoop, constituentHalfEdges);

      const { verts, heData } = buildCompositeLoopVertices(unionLoop, sorted);

      if (verts.length >= 6) {
        const loop = polyLine2New(verts);
        if (polyLine2WindingDir(loop) === WindingDir.CW) {
          polyLine2Reverse(loop);
          heData.reverse();
        }
        if (polyLine2Area(loop) > ZERO_EPSILON) {
          constructedExteriorPolyLines.push(loop);
          constructedExteriorHalfEdges.push(heData);
        }
      }
    }

    return {
      offsetExterior: unionOffsetExterior(allOffsetExterior),
      exteriorWallSegments: constructedExteriorPolyLines,
      exteriorWallHalfEdges: constructedExteriorHalfEdges,
      interiorWallSegments: allInteriorSegments,
    };
  } catch (error) {
    // Sentry is not a dependency of building-generation; log to console so the failure is visible
    console.error("Failed to merge composite exterior wall segments, falling back to unmerged data", error);
    return {
      offsetExterior: allOffsetExterior,
      exteriorWallSegments: allExteriorSegments,
      exteriorWallHalfEdges: allExteriorHalfEdges,
      interiorWallSegments: allInteriorSegments,
    };
  }
}

/**
 * Decomposes each exterior wall segment PolyLine2 from composite member levels into line segments and
 * performs a test to include/exclude each line segment from the composite exterior.
 */
function extractExteriorSegmentsFromCompositeLoop(
  allExteriorSegments: PolyLine2[],
  allExteriorHalfEdges: number[][],
  unionLoop: PolyLine2,
): { constituentSegments: PolyLine2[]; constituentHalfEdges: number[] } {
  const constituentSegments: PolyLine2[] = [];
  const constituentHalfEdges: number[] = [];
  for (let segIdx = 0; segIdx < allExteriorSegments.length; segIdx++) {
    const exteriorSegment = allExteriorSegments[segIdx];
    const segmentHalfEdgeData = allExteriorHalfEdges[segIdx];
    for (let i = 0; i < exteriorSegment.length - 2; i += 2) {
      const x0 = exteriorSegment[i];
      const y0 = exteriorSegment[i + 1];
      const x1 = exteriorSegment[i + 2];
      const y1 = exteriorSegment[i + 3];
      const edgeHalfEdge = segmentHalfEdgeData?.[i] ?? 0;

      const segmentPolyLine2 = polyLine2ByTwoPoints(x0, y0, x1, y1);

      const segmentPolyLine2Classification = classifyContainmentPolyLine2PolyLine2(segmentPolyLine2, unionLoop);

      if (segmentPolyLine2Classification === PolyLine2PolyLine2ContainmentClass.COINCIDENT) {
        constituentSegments.push(segmentPolyLine2);
        constituentHalfEdges.push(edgeHalfEdge);
      } else if (
        segmentPolyLine2Classification === PolyLine2PolyLine2ContainmentClass.INSIDE_COINCIDENT ||
        segmentPolyLine2Classification === PolyLine2PolyLine2ContainmentClass.OUTSIDE_COINCIDENT ||
        // Crossing segments may have some coincident overlap with the exterior union.
        segmentPolyLine2Classification === PolyLine2PolyLine2ContainmentClass.CROSSING
      ) {
        const splitSegments = splitPolyLine2ByRegions(segmentPolyLine2, [[unionLoop]]);

        const coincidentSegments = splitSegments
          .map(({ component }) => component)
          .filter((component) => {
            const [mx, my] = polyLine2EdgeEval(component, 0, 0.5, true);
            return (
              classifyContainmentVec2PolyLine2(mx, my, unionLoop, 0.1) === Vec2PolyLine2ContainmentClass.COINCIDENT
            );
          });

        if (coincidentSegments.length > 0) {
          // Intentionally collapse disjoint coincident segments into one extent. In practice,
          // tiny gaps here are usually unwanted perturbances from degenerate composite
          // geometry, and preserving each fragment creates noisy micro-segments.
          // Project each coincident segment's endpoints onto the original segment direction
          // and take the union of their extents to produce a single merged segment.
          let tMin = Infinity;
          let tMax = -Infinity;
          for (const coincidentSegment of coincidentSegments) {
            const lastIdx = coincidentSegment.length - 2;
            const t0 = closestParamNormalizedLine2(x0, y0, x1, y1, coincidentSegment[0], coincidentSegment[1], false);
            const t1 = closestParamNormalizedLine2(
              x0,
              y0,
              x1,
              y1,
              coincidentSegment[lastIdx],
              coincidentSegment[lastIdx + 1],
              false,
            );
            tMin = Math.min(tMin, t0, t1);
            tMax = Math.max(tMax, t0, t1);
          }
          const [mergedX0, mergedY0] = line2Eval(x0, y0, x1, y1, tMin, true);
          const [mergedX1, mergedY1] = line2Eval(x0, y0, x1, y1, tMax, true);
          constituentSegments.push(polyLine2ByTwoPoints(mergedX0, mergedY0, mergedX1, mergedY1));
          constituentHalfEdges.push(edgeHalfEdge);
        }
      }
    }
  }
  return { constituentSegments, constituentHalfEdges };
}

function unionOffsetExterior(offsetExteriors: PolyLine2[]): PolyLine2[] {
  const valid = offsetExteriors.filter((pl) => pl && polyLine2Area(pl) > ZERO_EPSILON);
  if (valid.length <= 1) {
    return valid;
  }

  try {
    const unioned = polygonUnion(valid.map((pl) => [polyLine2SetCCW(pl)]))
      .map((poly: PolyLine2Polygon) => poly[0])
      .filter((loop): loop is PolyLine2 => !!loop && polyLine2Area(loop) > ZERO_EPSILON);

    return unioned.length > 0 ? unioned : valid;
  } catch {
    return valid;
  }
}

interface SortedLoopSegment {
  seg: PolyLine2;
  halfEdge: number;
  startPos: number;
  endPos: number;
}

function normalizeLoopPosition(position: number, anchorPosition: number, loopLength: number): number {
  let normalized = position;
  while (normalized < anchorPosition - DIST_EPSILON) {
    normalized += loopLength;
  }
  return normalized;
}

function appendVertexWithHalfEdge(verts: number[], halfEdges: number[], x: number, y: number, halfEdge: number): void {
  if (verts.length >= 2 && vec2Dist(verts[verts.length - 2], verts[verts.length - 1], x, y) < DIST_EPSILON) {
    return;
  }
  verts.push(x, y);
  halfEdges.push(halfEdge, halfEdge);
}

/**
 * Iterates over sorted exterior line segments and projects each segment onto the unioned
 * input PolyLine. If an incoming line segment overlaps with an existing segment in the loop,
 * only the non-overlapping vertex is pushed onto the output loop.
 *
 * If there is a gap between exterior line segments, the intermediate vertex from the unioned
 * input PolyLine is inserted into the output loop, ensuring that the output geometry is coincident with
 * the input.
 */
function buildCompositeLoopVertices(
  loop: PolyLine2,
  sortedSegments: SortedLoopSegment[],
): { verts: number[]; heData: number[] } {
  const edgeCount = polyLine2EdgeCount(loop);
  const edgeStarts: number[] = [0];
  for (let edgeIndex = 0; edgeIndex < edgeCount; edgeIndex++) {
    const edgeLength = vec2Dist(
      loop[edgeIndex * 2],
      loop[edgeIndex * 2 + 1],
      loop[edgeIndex * 2 + 2],
      loop[edgeIndex * 2 + 3],
    );
    edgeStarts.push(edgeStarts[edgeIndex] + edgeLength);
  }
  const loopLength = edgeStarts[edgeCount];

  if (sortedSegments.length === 0 || loopLength <= DIST_EPSILON) {
    return { verts: [], heData: [] };
  }

  const firstSegment = sortedSegments[0];
  const anchorStartPos = firstSegment.startPos;
  const normalizePos = (position: number) => normalizeLoopPosition(position, anchorStartPos, loopLength);

  const normalizedLoopVertices: { x: number; y: number; pos: number }[] = [];
  for (let vertexIndex = 0; vertexIndex < edgeCount; vertexIndex++) {
    normalizedLoopVertices.push({
      x: loop[vertexIndex * 2],
      y: loop[vertexIndex * 2 + 1],
      pos: normalizePos(edgeStarts[vertexIndex]),
    });
  }
  const expandedLoopVertices = normalizedLoopVertices
    .flatMap((vertex) => [vertex, { ...vertex, pos: vertex.pos + loopLength }])
    .sort((a, b) => a.pos - b.pos);

  const verts: number[] = [];
  const heData: number[] = [];
  appendVertexWithHalfEdge(verts, heData, firstSegment.seg[0], firstSegment.seg[1], firstSegment.halfEdge);

  const closingEndPos = normalizePos(firstSegment.startPos) + loopLength;
  let cursorPos = normalizePos(firstSegment.startPos);
  const appendLoopVerticesBetween = (startPos: number, endPos: number, halfEdge: number) => {
    for (const loopVertex of expandedLoopVertices) {
      if (loopVertex.pos <= startPos + DIST_EPSILON) {
        continue;
      }
      if (loopVertex.pos >= endPos - DIST_EPSILON) {
        break;
      }
      appendVertexWithHalfEdge(verts, heData, loopVertex.x, loopVertex.y, halfEdge);
    }
  };

  for (const segment of sortedSegments) {
    const segmentStartPos = normalizePos(segment.startPos);
    let segmentEndPos = normalizePos(segment.endPos);

    if (segmentEndPos < segmentStartPos - DIST_EPSILON) {
      segmentEndPos += loopLength;
    }
    if (segmentEndPos <= cursorPos + DIST_EPSILON) {
      continue;
    }

    if (segmentStartPos > cursorPos + DIST_EPSILON) {
      appendLoopVerticesBetween(cursorPos, segmentStartPos, segment.halfEdge);
      appendVertexWithHalfEdge(verts, heData, segment.seg[0], segment.seg[1], segment.halfEdge);
    }
    appendVertexWithHalfEdge(verts, heData, segment.seg[2], segment.seg[3], segment.halfEdge);

    cursorPos = segmentEndPos;
    if (cursorPos >= closingEndPos - DIST_EPSILON) {
      break;
    }
  }

  const lastHalfEdge = sortedSegments[sortedSegments.length - 1].halfEdge;
  appendLoopVerticesBetween(cursorPos, closingEndPos, lastHalfEdge);
  appendVertexWithHalfEdge(verts, heData, firstSegment.seg[0], firstSegment.seg[1], firstSegment.halfEdge);

  return { verts, heData };
}

/**
 * Orders line segments by their midpoint's CCW position along a closed loop.
 */
function sortSegmentsByLoopPosition(segments: PolyLine2[], loop: PolyLine2, halfEdges?: number[]): SortedLoopSegment[] {
  const edgeCount = polyLine2EdgeCount(loop);

  const edgeStarts: number[] = [0];
  for (let edgeIndex = 0; edgeIndex < edgeCount; edgeIndex++) {
    const edgeLength = vec2Dist(
      loop[edgeIndex * 2],
      loop[edgeIndex * 2 + 1],
      loop[edgeIndex * 2 + 2],
      loop[edgeIndex * 2 + 3],
    );
    edgeStarts.push(edgeStarts[edgeIndex] + edgeLength);
  }
  const loopLength = edgeStarts[edgeCount];

  const withPos: (SortedLoopSegment & { pos: number })[] = [];

  for (let segmentIndex = 0; segmentIndex < segments.length; segmentIndex++) {
    let orientedSegment = segments[segmentIndex];
    const segmentHalfEdge = halfEdges?.[segmentIndex] ?? 0;
    const sx = orientedSegment[0];
    const sy = orientedSegment[1];
    const ex = orientedSegment[2];
    const ey = orientedSegment[3];
    const mx = 0.5 * (sx + ex);
    const my = 0.5 * (sy + ey);
    const bestEdge = closestPointPolyLine2(loop, mx, my).edgeIndex;

    const ux0 = loop[bestEdge * 2];
    const uy0 = loop[bestEdge * 2 + 1];
    const ux1 = loop[bestEdge * 2 + 2];
    const uy1 = loop[bestEdge * 2 + 3];
    const edgeLength = edgeStarts[bestEdge + 1] - edgeStarts[bestEdge];
    let tStart = closestParamNormalizedLine2(ux0, uy0, ux1, uy1, sx, sy, false);
    let tEnd = closestParamNormalizedLine2(ux0, uy0, ux1, uy1, ex, ey, false);

    if (tStart > tEnd + DIST_EPSILON) {
      orientedSegment = polyLine2ByTwoPoints(ex, ey, sx, sy);
      const swap = tStart;
      tStart = tEnd;
      tEnd = swap;
    }

    const startPos = edgeStarts[bestEdge] + tStart * edgeLength;
    let endPos = edgeStarts[bestEdge] + tEnd * edgeLength;
    if (endPos < startPos - DIST_EPSILON) {
      endPos += loopLength;
    }

    withPos.push({
      seg: orientedSegment,
      halfEdge: segmentHalfEdge,
      startPos,
      endPos,
      pos: 0.5 * (startPos + endPos),
    });
  }

  withPos.sort((a, b) => a.pos - b.pos);
  return withPos.map((segment) => ({
    seg: segment.seg,
    halfEdge: segment.halfEdge,
    startPos: segment.startPos,
    endPos: segment.endPos,
  }));
}

function wallGapSegments(
  wall: SolidWall,
  includeDoors: boolean,
  includeWindows: boolean,
  includeExteriorRooms: boolean,
  excludeUndimensionedOpenings: boolean,
): PolyLine2 {
  const { leftRoom } = getWallPath(wall);
  const leftSide = roomIsEffectivelyExterior(leftRoom, includeExteriorRooms);

  const startSortedGaps: StartSortedWallGaps = solidWallGaps({
    wall,
    isLeftSide: leftSide,
    normalized: false,
    excludeUndimensionedOpenings,
    includeDoors,
    includeWindows,
  });

  const { edge, gaps } = startSortedGaps;
  const segments = polyLine2New(gaps.length * 2 + 4);
  // Re-sort by center in reverse order
  // TODO: this could probably be done by the existing sort order by start of gaps.
  //       Instead of reversing, the `for` cycle below could go in reverse.
  gaps.sort((a, b) => b.center - a.center);

  segments[0] = edge[edge.length - 2];
  segments[1] = edge[edge.length - 1];

  for (let i = 0, li = gaps.length; i < li; i++) {
    const [rightX, rightY] = polyLine2EdgeEval(edge, 0, gaps[i].center);

    segments[i * 2 + 2] = rightX;
    segments[i * 2 + 3] = rightY;
  }

  segments[segments.length - 2] = edge[0];
  segments[segments.length - 1] = edge[1];

  return segments;
}

const WALL_SEGMENT_COLLINEARITY_TOL = 10 * DIST_EPSILON;

/**
 * This function looks for a T junctions at an exterior vertex and determines the offset needed for
 * dimensioning exterior walls.
 * @param excludedNonParallelWalls A table of half edges to exclude from the search. Used to exclude
 * interior walls with disabled dimensions.
 */
function nonParallelSolidWallAtVertex(
  planarGraph: PlanarGraph,
  walls: WallMassSystem,
  vertex: number,
  halfEdge: number,
  excludedNonParallelWalls: HalfEdgeAssociationTable<EdgeDimensionBehavior> | true,
): { foundSolidWall: boolean; nOffset: number; foundWallChange: boolean; disableDimension: boolean } {
  const heWall = walls.walls[walls.halfEdgeToWall[halfEdge]];
  if (heWall.wallType !== WallType.SOLID_MASS) {
    return { foundSolidWall: false, nOffset: 0, foundWallChange: false, disableDimension: false };
  }

  const [heNx, heNy] = planarGraphHalfEdgeNormal(planarGraph, halfEdge);
  const heWallEdge = solidWallMassDimEdge(heWall);
  if (!heWallEdge) {
    return { foundSolidWall: false, nOffset: 0, foundWallChange: false, disableDimension: false };
  }

  let ignoreVertex = false;
  let foundParallelContinuousExteriorWall = false;

  let foundParallelSolidWall = false;
  let parallelKind: SolidWallKind | undefined = undefined;
  let parallelThicknessChange = false;

  let foundNonParallelSolidWall = false;
  let nonParallelKind: SolidWallKind | undefined = undefined;
  let nonParallelOffset = 0;

  for (const vHalfEdge of planarGraph.vertexHalfEdges[vertex]) {
    if (vHalfEdge === halfEdge) {
      continue;
    }
    const vWallIndex = walls.halfEdgeToWall[vHalfEdge];
    const vWall = walls.walls[vWallIndex];
    if (vWall.wallType !== WallType.SOLID_MASS) {
      continue;
    }

    const vWallEdge = solidWallMassDimEdge(vWall);
    if (!vWallEdge) {
      continue;
    }

    const [vHeNx, vHeNy] = planarGraphHalfEdgeNormal(planarGraph, vHalfEdge);
    const dot = Math.abs(vec2Dot(vHeNx, vHeNy, heNx, heNy));

    const wallKind = determineWallKind(vWall.path.rightRoomKind, vWall.path.leftRoomKind);

    // exclude parallel walls
    if (dot > ONE_MINUS_DIST_EPSILON) {
      foundParallelSolidWall = true;

      if (!parallelKind || wallKind > parallelKind) {
        parallelKind = wallKind;
        const parallelCollinear = line2Collinear(
          vWallEdge[0],
          vWallEdge[1],
          vWallEdge[2],
          vWallEdge[3],
          heWallEdge[0],
          heWallEdge[1],
          heWallEdge[2],
          heWallEdge[3],
          WALL_SEGMENT_COLLINEARITY_TOL,
        );

        if (!parallelCollinear) {
          parallelThicknessChange = true;
        } else {
          foundParallelContinuousExteriorWall = true;
        }
      }
    } else {
      foundNonParallelSolidWall = true;

      // only consider disabling dimensions if vWall is interior
      if (wallKind === SolidWallKind.INTERIOR) {
        if (typeof excludedNonParallelWalls === "boolean" && excludedNonParallelWalls === true) {
          ignoreVertex = true;
        } else {
          const halfEdgeDimDisabled = (halfEdge: number) => {
            return excludedNonParallelWalls[halfEdge]?.find((x) => x.item.disableExteriorAuto);
          };

          if (halfEdgeDimDisabled(vHalfEdge) || halfEdgeDimDisabled(planarGraphAdjacentHalfEdge(vHalfEdge))) {
            ignoreVertex = true;
          }
        }
      }

      // exclude non perp walls - we just leave a 0 offset for now
      if (dot > DIST_EPSILON) {
        continue;
      }

      if (!nonParallelKind || wallKind > nonParallelKind) {
        nonParallelKind = wallKind;
        nonParallelOffset = projectToHalfEdge(vWallEdge, heWallEdge);
      }
    }
  }

  // in the event that there is no parallel reference edge,
  // we need to zero the offset
  if (!foundParallelSolidWall) {
    nonParallelOffset = 0;
  }

  // // TODO: handle thickness changes at void walls
  // if (foundParallelSolidWall && !foundNonParallelSolidWall && Math.abs(parallelOffset) > DIST_EPSILON) {
  //   foundNonParallelSolidWall = true;
  //   nonParallelOffset = parallelOffset;
  // }
  // in the event that this half edge is smaller than the offset,
  // we need to account for it
  const heEdgeLen = polyLine2Length(heWallEdge);
  if (Math.abs(nonParallelOffset) > heEdgeLen) {
    nonParallelOffset = -heEdgeLen;
  }

  // only consider ignoring the vertex if a parallel wall was found
  ignoreVertex &&= foundParallelContinuousExteriorWall;

  return {
    foundSolidWall: foundNonParallelSolidWall,
    nOffset: nonParallelOffset,
    foundWallChange: parallelThicknessChange,
    disableDimension: ignoreVertex,
  };
}

// projects the test edge start point to the target edge,
// then calculate the distance between that projected point and the closest
// end point as the offset for the wall dimensions at that vertex.
// This method will only work for 90 degee angles
function projectToHalfEdge(testEdge: PolyLine2, targetEdge: PolyLine2) {
  const [sptX, sptY, eptX, eptY] = targetEdge;

  const [eX, eY] = [testEdge[0], testEdge[1]];
  const t = closestParamNormalizedLine2(sptX, sptY, eptX, eptY, eX, eY, false);
  const [cptX, cptY] = line2Eval(sptX, sptY, eptX, eptY, t, true);

  let optX: number;
  let optY: number;
  if (t < 0.5) {
    [optX, optY] = [sptX, sptY];
  } else {
    [optX, optY] = [eptX, eptY];
  }

  let projectedDist = vec2Dist(optX, optY, cptX, cptY);
  if (t < 0 || t > 1) {
    projectedDist = -projectedDist;
  }
  return projectedDist;
}

function solidWallMassDimEdge(wall: SolidWallMass): PolyLine2 | null {
  const { path, leftEdge, rightEdge } = wall;
  const { leftRoom, rightRoom } = path;
  const leftExterior = roomIsEffectivelyExterior(leftRoom, false);
  const rightExterior = roomIsEffectivelyExterior(rightRoom, false);
  if (leftRoom && rightRoom) {
    if (leftExterior !== rightExterior) {
      return leftExterior ? leftEdge : rightEdge;
    }
    return solidWallMassPickOrientedDimEdge(wall);
  } else if (!leftRoom && !rightRoom) {
    return null;
  } else {
    return leftRoom ? rightEdge : leftEdge;
  }
}

// returns the appropriate wall edge for T junction walls
// to be placed on the exterior dimension string
// based on the orientation of the polyline.
// Dims should be taken from the BOTTOM LEFT corner
function solidWallMassPickOrientedDimEdge(wall: SolidWallMass) {
  const { leftEdge, rightEdge } = wall;
  const [rnX, rnY] = polyLine2EdgeNormal(rightEdge, 0);
  const isHorizontal = Math.abs(rnX) < Math.abs(rnY);

  if (isHorizontal) {
    const ly = leftEdge[1];
    const ry = rightEdge[rightEdge.length - 1];
    if (ly < ry) {
      return leftEdge;
    } else {
      return rightEdge;
    }
  } else {
    const lx = leftEdge[0];
    const rx = rightEdge[rightEdge.length - 2];
    if (lx < rx) {
      return leftEdge;
    } else {
      return rightEdge;
    }
  }
}

interface FormatRoomLabelDimTextParams extends Pick<PlanSettings, "roomLabelRounding" | "roomLabelUnits"> {
  hideDims: boolean;
  roomName: string;
  widthOverride?: string;
  heightOverride?: string;
  width: number;
  height: number;
  upperCase?: boolean;
}

export function formatRoomDims(
  width: number,
  height: number,
  roomLabelUnits: RoomLabelUnits,
  roomLabelRounding: RoomLabelRounding,
) {
  const roundedHeight = convertAndRoundInches(height, roomLabelUnits, roomLabelRounding);
  const roundedWidth = convertAndRoundInches(width, roomLabelUnits, roomLabelRounding);

  return `${roundedWidth} x ${roundedHeight}`;
}

export function formatRoomArea(area: number, roomLabelRounding: RoomLabelRounding) {
  const areaSquareInches = inchesSquaredNew(area);
  return convertAndRoundSquaredInches(areaSquareInches, roomLabelRounding);
}

export function formatRoomLabelDimText({
  hideDims,
  roomName,
  width,
  widthOverride,
  height,
  heightOverride,
  roomLabelUnits,
  roomLabelRounding,
  upperCase = true,
}: FormatRoomLabelDimTextParams): string {
  if (hideDims) {
    return upperCase ? roomName.toUpperCase() : roomName;
  }

  let dimText;

  const roundedHeight = convertAndRoundInches(height, roomLabelUnits, roomLabelRounding);
  const roundedWidth = convertAndRoundInches(width, roomLabelUnits, roomLabelRounding);

  if (widthOverride !== undefined && heightOverride !== undefined) {
    dimText = `${widthOverride} x ${heightOverride}`;
  } else {
    dimText = `${roundedWidth} x ${roundedHeight}`;
  }

  return (upperCase ? roomName.toUpperCase() : roomName).concat(`\n${dimText}`);
}

export function convertAndRoundInches(
  decimalInches: number,
  units: RoomLabelUnits,
  rounding: RoomLabelRounding,
): string {
  if (units === RoomLabelUnits.WHOLE_FEET) {
    const decimalFeet = decimalInches / 12;
    let roundedFeet: number;
    switch (rounding) {
      case RoomLabelRounding.LOWEST:
        roundedFeet = Math.floor(decimalFeet);
        break;
      case RoomLabelRounding.NEAREST:
        roundedFeet = Math.round(decimalFeet);
        break;
      case RoomLabelRounding.HIGHEST:
        roundedFeet = Math.ceil(decimalFeet);
        break;
    }
    return `${roundedFeet}'`;
  }

  const feet = Math.floor(decimalInches / 12);
  const remainingInches = decimalInches - feet * 12;
  let roundedInches;

  if (rounding === RoomLabelRounding.LOWEST) {
    roundedInches = Math.floor(remainingInches);
  } else if (rounding === RoomLabelRounding.NEAREST) {
    roundedInches = Math.round(remainingInches);
  } else if (rounding === RoomLabelRounding.HIGHEST) {
    roundedInches = Math.ceil(remainingInches);
  }

  if (roundedInches === 12) {
    return `${feet + 1}'-0"`;
  }
  return `${feet}'-${roundedInches}"`;
}

export function convertAndRoundSquaredInches(decimalInches: InchesSquared, rounding: RoomLabelRounding): string {
  const decimalFeet = feetSquaredFromInchesSquared(decimalInches);
  let roundedSquareFeet: number;
  switch (rounding) {
    case RoomLabelRounding.LOWEST:
      roundedSquareFeet = Math.floor(decimalFeet);
      break;
    case RoomLabelRounding.NEAREST:
      roundedSquareFeet = Math.round(decimalFeet);
      break;
    case RoomLabelRounding.HIGHEST:
      roundedSquareFeet = Math.ceil(decimalFeet);
      break;
  }
  return `${roundedSquareFeet} sq ft`;
}
